import { useContext } from "react";
import { Material, MaterialOrder, ProductOrder, Provider } from "../../../data/entities";
import { MATERIAL_ORDER_STATUS, ROLE } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { api } from "../../../shared/hooks/api";
import apiMethods from "../../../utils/api";
import Note from "../../ProductOrder/single/Note";
import Priority from "../../ProductOrder/single/Priority";
import { DoubleColumnWrapper, SecondColumn } from "../../ProductOrder/single/Styled";
import { Assignees } from "./Assignees";
import { MaterialList } from "./MaterialList";
import { ProductOrderLink } from "./ProductOrderLink";
import ProviderInfo from "./ProviderInfo";
import { SingleMaterialContentWrapper } from "./Styled";
import Summary from "./Summary";
import Upload from "./Upload";

interface SingleProductOrderProps {
    materialOrder: MaterialOrder;
    mergeMaterialOrder: Function;
    productOrder?: ProductOrder;
    provider?: Provider | null;
    sourceMaterials?: Material[];
    fetchMaterialOrder?: any;
    isView?: boolean;
}

export const SingleMaterialOrder = ({
                                        materialOrder,
                                        mergeMaterialOrder,
                                        productOrder,
                                        provider,
                                        sourceMaterials,
                                        fetchMaterialOrder,
                                        isView,
                                    }: SingleProductOrderProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const updateMaterialOrder = async (data: any) => {
        await apiMethods.patch(`/materials/orders/${materialOrder?.id}/`, data);
    };
    const shouldFetchProvider = !!materialOrder.provider_id && !provider;
    const [{ data: fetchedProvider }] = api.get(
        `/materials/providers/${materialOrder.provider_id}/`,
        undefined,
        !shouldFetchProvider,
    );
    const orderProvider = provider || materialOrder.provider || fetchedProvider || null;

    const linkedProductOrder = materialOrder.product_order
        ? materialOrder.product_order
        : productOrder;

    const canSeeSummary = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    const assigneeCases = () => {

        const requiredStorageKeepers = materialOrder.required_storage_keepers || [];

        const storageKeeperAssignments: any[] = requiredStorageKeepers
            .map((keeper) => {
                if (keeper === "first_storage_keeper") {
                    return {
                        title: "Thủ kho 1",
                        fieldName: "first_storage_keeper",
                        role: "first_storage_keeper",
                    };
                } else if (keeper === "second_storage_keeper") {
                    return {
                        title: "Thủ kho 2",
                        fieldName: "second_storage_keeper",
                        role: "second_storage_keeper",
                    };
                }
                return null; // Return null for unsupported roles
            })
            .filter((assignment: any) => assignment !== null);

        if ([MATERIAL_ORDER_STATUS.IMPORTING,MATERIAL_ORDER_STATUS.RESTOCK, MATERIAL_ORDER_STATUS.OPEN].includes(materialOrder.status as MATERIAL_ORDER_STATUS)) {
                return [
                    {
                        title: "Nhân viên hậu cần mua vật tư",
                        fieldName: "purchasing_staff",
                        role: "purchasing_staff",
                    },
                    // {
                    //     title: "Quản lý logistics",
                    //     fieldName: "logistic_manager",
                    //     role: "logistic_manager",
                    // },
                    ...storageKeeperAssignments,
                ];
        }
        return [];
    };

    return (
        <SingleMaterialContentWrapper>
            {linkedProductOrder && (
                <ProductOrderLink productOrder={linkedProductOrder} />
            )}
            <MaterialList
                materials={materialOrder.materials as any}
                mergeMaterialOrder={mergeMaterialOrder}
                isView={isView}
                hasProductOrder={!!linkedProductOrder}
                providerId={materialOrder.provider_id}
                providerName={materialOrder.provider_name}
                sourceMaterials={sourceMaterials}
            />

            <DoubleColumnWrapper>
                <SecondColumn>
                    <ProviderInfo
                        provider={orderProvider}
                        providerName={materialOrder.provider_name}
                    />
                    {canSeeSummary && (
                        <Summary data={materialOrder.materials} />
                    )}
                    {materialOrder.id && (
                        <Upload
                            model="materials.MaterialOrder"
                            objectId={materialOrder.id}
                            objectName={materialOrder.name ? String(materialOrder.name) : undefined}
                        />
                    )}
                </SecondColumn>
                <SecondColumn>
                    {!isView && (
                        <Priority
                            productOrder={materialOrder as any}
                            mergeProductOrder={mergeMaterialOrder as any}
                        />
                    )}

                    {isView && (
                        <Assignees
                            data={materialOrder}
                            updateData={updateMaterialOrder}
                            fetchData={fetchMaterialOrder}
                            mergeMaterialOrder={mergeMaterialOrder}
                            fields={assigneeCases()}
                        />
                    )}

                    <Note
                        text={materialOrder.note}
                        setText={(value: string) =>
                            mergeMaterialOrder({ note: value })
                        }
                        isView={isView}
                    />
                </SecondColumn>
            </DoubleColumnWrapper>
        </SingleMaterialContentWrapper>
    );
};
